/**
 * Shared Version ID column text for version history tables and CSV.
 * Prefers each row's breadcrumb / "For …" comment / version_content fields;
 * parent breadcrumb is fallback only (sparse API rows like CREATE_STEP + ids).
 */

import {
  resolveNodePathLabelsFromRevisionTree,
  augmentHistoryPathSegmentsWithTree,
} from "./resolveNodePathFromRevisionTree.js";
import {
  mergeAuditVersionContent,
  normalizeAuditPartNumber,
  parsePartRevFromAuditActionMessage,
} from "../../pages/AuditTrail/utils/auditTrailProcedureIdentity.js";

/** Decode HTML entities (e.g. &gt; → >). Multi-pass for &amp;gt;-style data. */
export function decodeHtmlEntitiesForDisplay(str) {
  if (typeof str !== "string" || str === "") return str;
  if (typeof document !== "undefined") {
    const t = document.createElement("textarea");
    let cur = str;
    let prev = null;
    for (let i = 0; i < 6 && cur !== prev; i += 1) {
      prev = cur;
      t.innerHTML = cur;
      cur = t.value;
    }
    return cur;
  }
  return str
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
}

/** Collapse empty " > > > " segments; keep trailing " > Version {uuid}". */
export function collapseRedundantPathArrows(displayStr) {
  if (typeof displayStr !== "string") return displayStr;
  const decoded = decodeHtmlEntitiesForDisplay(displayStr);
  const tailMatch = decoded.match(/\s*>\s*Version\s+.+$/);
  if (tailMatch) {
    const base = decoded.slice(0, tailMatch.index).trim();
    const tail = tailMatch[0].trim();
    const collapsed = base
      .split(/\s*>\s*/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(" > ");
    return [collapsed, tail].filter(Boolean).join(" ");
  }
  return decoded
    .split(/\s*>\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" > ");
}

function contextPathFromAuditComment(comment) {
  if (typeof comment !== "string" || !comment.trim()) return null;
  const line = comment.split(/\r?\n/)[0].trim();
  const m = line.match(/^For\s+(.+)/i);
  const path = decodeHtmlEntitiesForDisplay((m ? m[1] : line).trim());
  return path.includes(">") ? path : null;
}

function pickStr(...candidates) {
  for (const v of candidates) {
    if (v == null) continue;
    const t = String(v).trim();
    if (t) return t;
  }
  return null;
}

/**
 * When version_content only has partNumber + rev (many node saves omit section/step),
 * parse "Section 1" / "Step 2" / "Substep …" from long-form action / action_to_show.
 */
function collectPathSegmentsFromAuditText(row, vc, pvc) {
  const text = [
    row?.action,
    row?.comment,
    vc?.action_to_show,
    pvc?.action_to_show,
  ]
    .filter((x) => typeof x === "string" && x.trim())
    .join("\n");
  if (!text) return [];
  const out = [];
  const sec = text.match(/\b(Section\s+[\dA-Za-z._-]+)\b/i);
  if (sec) out.push(sec[1]);
  const st = text.match(/\b(Step\s+[\dA-Za-z._-]+)\b/i);
  if (st) out.push(st[1]);
  const sub = text.match(/\b(Substep|Sub-step)\s+([\dA-Za-z._-]+)\b/i);
  if (sub) {
    out.push(
      `Substep ${String(sub[2]).trim()}`,
    );
  }
  const rt = text.match(/\bRich[-\s]?Text\s+(\d+)\b/i);
  if (rt) out.push(`Rich Text ${String(rt[1]).trim()}`);
  const vm = text.match(/\bVideo\s+(\d+)\b/i);
  if (vm) out.push(`Video ${String(vm[1]).trim()}`);
  const att = text.match(/\bAttachment\s+(\d+)\b/i);
  if (att) out.push(`Attachment ${String(att[1]).trim()}`);
  const cv = text.match(/\bCanvas\s+(\d+)\b/i);
  if (cv) out.push(`Canvas ${String(cv[1]).trim()}`);
  return out;
}

/** e.g. "Edit Step 1 for 13310, Rev C-" -> node label "Step 1" */
function nodeLabelFromActionToShow(ats) {
  const s = String(ats || "").trim();
  const m = s.match(
    /^(?:Save|Edit|Update|Add|Delete|Create)\s+(.+?)\s+for\s+([^,]+),\s*Rev\s+(.+)$/i,
  );
  if (m) return m[1].trim();
  return null;
}

/** Procedure + Rev for the start of the path (vc, then prop e.g. "13310 > Rev C-"). */
function getProcedureHead(partFromFields, revPart, propTrim) {
  const pn = partFromFields || null;
  if (pn && revPart) return [String(pn).trim(), revPart];
  if (pn) return [String(pn).trim()];
  if (propTrim) {
    const s = String(propTrim)
      .split(/>/)
      .map((x) => x.trim())
      .filter(Boolean);
    if (s.length >= 2 && /^Rev\s/i.test(s[1])) {
      return [s[0], s[1]];
    }
    if (s.length === 1 && revPart) return [s[0], revPart];
  }
  return revPart ? [revPart] : [];
}

function dedupePathSegments(segs) {
  const out = [];
  for (const x of segs) {
    const t = x == null ? "" : String(x).trim();
    if (!t) continue;
    if (out[out.length - 1] === t) continue;
    out.push(t);
  }
  return out;
}

/**
 * When API `breadcrumb_text` is only "Section 1" (no item / Rev), prepend head from context
 * so we never show "Section 1 > Version …" without procedure + rev when those are known.
 */
function mergeHeadWithRowCrumb(head, rowCrumb) {
  if (!rowCrumb) return null;
  const c = rowCrumb
    .split(/>/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (c.length === 0) return null;
  const h = head && head.length ? head : [];
  if (!h.length) return c;
  if (/^Section\s/i.test(c[0] || "") || /^Step\s/i.test(c[0] || "") || /^Substep\s/i.test(c[0] || "")) {
    return dedupePathSegments([...h, ...c]);
  }
  if (c[0] && h[0] && c[0] === h[0]) {
    return c.length >= h.length ? c : dedupePathSegments([...h, ...c]);
  }
  return dedupePathSegments([...h, ...c]);
}

function escapeReFragment(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The revision tree can match multiple node ids on one history row (e.g. section + step
 * refs in version_content), and the resolver picks the longest path. That wrongly shows
 * "… > Section N > Step M" for a "Save Section N for …" row. Use action / action_to_show
 * only (not user free-text comment) to cap the path at the entity being saved.
 */
function trimDisplaySegmentsToRowActionIntent(segments, row) {
  if (!Array.isArray(segments) || segments.length === 0) return segments;
  const vc = row?.version_content || {};
  const pvc = row?.parent_version_data?.version_content || {};
  const text = [row?.action, vc?.action_to_show, pvc?.action_to_show]
    .filter((x) => typeof x === "string" && x.trim())
    .join("\n");
  if (!text.trim()) return segments;

  const stepSave = text.match(
    /(?:^|\n)(?:Save|Edit|Update|Add|Delete|Create)\s+Step\s+([\dA-Za-z._-]+)\s+for/i,
  );
  if (stepSave) {
    const n = String(stepSave[1]).trim();
    const re = new RegExp(`^Step\\s+${escapeReFragment(n)}\\b`, "i");
    const idx = segments.findIndex((s) => re.test(String(s).trim()));
    if (idx >= 0) return segments.slice(0, idx + 1);
    return segments;
  }

  const sectionSave = text.match(
    /(?:^|\n)(?:Save|Edit|Update|Add|Delete|Create)\s+Section\s+([\dA-Za-z._-]+)\s+for/i,
  );
  if (sectionSave) {
    const n = String(sectionSave[1]).trim();
    const re = new RegExp(`^Section\\s+${escapeReFragment(n)}\\b`, "i");
    const idx = segments.findIndex((s) => re.test(String(s).trim()));
    if (idx >= 0) return segments.slice(0, idx + 1);
  }

  return segments;
}

/**
 * @param {object} row - version history row
 * @param {string} [breadcrumbTextProp] - UI context fallback when row has no path (e.g. current node breadcrumb)
 * @param {Array|undefined} [revisionTreeNodes] - `allNodesData` / allRevisionNodes: matches `row.version_id` to `version_id_ref` for Section/Step path
 */
export function formatVersionHistoryIdDisplay(
  row,
  breadcrumbTextProp,
  revisionTreeNodes,
) {
  const vid = row?.version_id;
  const vc =
    mergeAuditVersionContent(row) ||
    (row?.version_content && typeof row.version_content === "object"
      ? row.version_content
      : {});
  const pvc =
    mergeAuditVersionContent(row?.parent_version_data) ||
    (row?.parent_version_data?.version_content &&
    typeof row.parent_version_data.version_content === "object"
      ? row.parent_version_data.version_content
      : {});

  const rowCrumb = String(vc.breadcrumb_text || pvc.breadcrumb_text || "").trim();
  const fromComment = contextPathFromAuditComment(row?.comment);
  const propTrim =
    breadcrumbTextProp != null && String(breadcrumbTextProp).trim() !== ""
      ? String(breadcrumbTextProp).trim()
      : "";

  const activityBlob = [
    row?.action,
    row?.action_to_show,
    vc?.action_to_show,
    pvc?.action_to_show,
  ]
    .filter((s) => typeof s === "string" && s.trim())
    .join("\n");
  const parsedPartRev = parsePartRevFromAuditActionMessage(activityBlob);

  let revRaw = vc.revision ?? vc.rev ?? pvc.revision ?? pvc.rev;
  if (revRaw == null || String(revRaw).trim() === "") {
    if (parsedPartRev.revision) revRaw = parsedPartRev.revision;
  }
  const revPart =
    revRaw != null && String(revRaw).trim() !== ""
      ? `Rev ${String(revRaw).trim()}`
      : null;

  const sectionField = pickStr(
    vc.section,
    vc.sectionTitle,
    vc.section_title,
    vc.section_name,
    pvc.section,
    pvc.sectionTitle,
    pvc.section_title,
    pvc.section_name,
  );
  const stepField = pickStr(
    vc.stepTitle,
    vc.step_title,
    vc.stepName,
    vc.global_step_label,
    pvc.stepTitle,
    pvc.step_title,
    pvc.stepName,
  );
  const pageOrLeaf = pickStr(
    vc.pageTitle,
    vc.page_title,
    vc.substepTitle,
    vc.substep_title,
    vc.video_label,
    vc.rich_text_label,
    vc.attachment_label,
    vc.canvas_label,
    pvc.pageTitle,
    pvc.page_title,
    pvc.substepTitle,
    pvc.substep_title,
  );

  const partNumberFromVc = pickStr(
    vc.partNumber,
    vc.part_number,
    vc.procedure_number,
    pvc.partNumber,
    pvc.part_number,
    pvc.procedure_number,
  );
  const part0 =
    (partNumberFromVc && String(partNumberFromVc).trim()) ||
    (parsedPartRev.partNumber
      ? normalizeAuditPartNumber(parsedPartRev.partNumber)
      : null) ||
    null;

  const fromFields = [part0, revPart, sectionField, stepField, pageOrLeaf]
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean);

  const fromAudit = collectPathSegmentsFromAuditText(row, vc, pvc);
  const atsNode =
    nodeLabelFromActionToShow(vc.action_to_show) ||
    nodeLabelFromActionToShow(pvc.action_to_show);

  const parts = [...fromFields];
  for (const seg of fromAudit) {
    if (!seg) continue;
    if (parts.includes(seg)) continue;
    if (
      parts.some(
        (f) =>
          f === seg || (f.length > 1 && seg.length > 1 && (f.includes(seg) || seg.includes(f))),
      )
    ) {
      continue;
    }
    parts.push(seg);
  }
  if (atsNode) {
    const already = parts.some(
      (f) =>
        f === atsNode ||
        (atsNode.length > 1 && f.includes(atsNode)) ||
        (f.length > 1 && atsNode.includes(f)),
    );
    if (!already) {
      parts.push(atsNode);
    }
  }

  const fromTree =
    Array.isArray(revisionTreeNodes) && revisionTreeNodes.length > 0
      ? resolveNodePathLabelsFromRevisionTree(revisionTreeNodes, row)
      : null;

  const head = getProcedureHead(part0, revPart, propTrim);

  let displaySegs = null;

  if (fromTree && fromTree.length > 0) {
    displaySegs = dedupePathSegments([...head, ...fromTree]);
  } else if (rowCrumb) {
    displaySegs = mergeHeadWithRowCrumb(head, rowCrumb);
  } else if (parts.length > 0) {
    displaySegs = dedupePathSegments(parts);
    if (
      displaySegs.length > 0 &&
      /^Section\s/i.test(displaySegs[0]) &&
      head.length > 0
    ) {
      displaySegs = dedupePathSegments([...head, ...displaySegs]);
    }
  }

  if (displaySegs && displaySegs.length > 0) {
    displaySegs = trimDisplaySegmentsToRowActionIntent(displaySegs, row);
    if (
      Array.isArray(revisionTreeNodes) &&
      revisionTreeNodes.length > 0
    ) {
      displaySegs = augmentHistoryPathSegmentsWithTree(
        displaySegs,
        revisionTreeNodes,
      );
      displaySegs = dedupePathSegments(displaySegs);
    }
  }

  const rowHeadSegsOnly = getProcedureHead(part0, revPart, "");
  const rowHeadStr =
    rowHeadSegsOnly.length > 0 ? rowHeadSegsOnly.join(" > ") : "";

  let raw;
  if (displaySegs && displaySegs.length > 0) {
    raw =
      vid != null && vid !== ""
        ? `${displaySegs.join(" > ")} > Version ${vid}`
        : displaySegs.join(" > ");
  } else if (fromComment && vid != null && vid !== "") {
    raw = `${fromComment} > Version ${vid}`;
  } else if (rowHeadStr && vid != null && vid !== "") {
    raw = `${rowHeadStr} > Version ${vid}`;
  } else if (propTrim) {
    raw =
      vid != null && vid !== ""
        ? `${propTrim} > Version ${vid}`
        : propTrim;
  } else {
    raw = vid != null && vid !== "" ? `Version ${vid}` : "-";
  }

  return collapseRedundantPathArrows(raw);
}
